/**
 * Утилиты для работы с валютами и конвертацией на клиенте
 */

// Нормализует коды валют (база данных использует 'uah'/'usd', UI использует 'UAH'/'USD')
const normalizeCurrency = (currency) => {
    if (!currency) return 'UAH';
    return currency.toUpperCase();
};

const EXCHANGE_RATES_STORAGE_KEY = 'exchangeRatesV1';
const KNOWN_USD_RATE_STORAGE_KEY = 'knownUsdRateV1';

const DEFAULT_EXCHANGE_RATES = {
    'USD/UAH': 40.5,
    'UAH/USD': 1 / 40.5,
    'EUR/UAH': 40.0,
    'UAH/EUR': 1 / 40.0,
    'UAH/UAH': 1.0,
    'USD/USD': 1.0,
    'EUR/EUR': 1.0,
};

let runtimeExchangeRates = { ...DEFAULT_EXCHANGE_RATES };

const loadRatesFromStorage = () => {
    try {
        const raw = localStorage.getItem(EXCHANGE_RATES_STORAGE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
            runtimeExchangeRates = {
                ...DEFAULT_EXCHANGE_RATES,
                ...parsed,
            };
        }
    } catch (error) {
        runtimeExchangeRates = { ...DEFAULT_EXCHANGE_RATES };
    }
};

loadRatesFromStorage();

const saveRatesToStorage = (rates) => {
    try {
        localStorage.setItem(EXCHANGE_RATES_STORAGE_KEY, JSON.stringify(rates));
    } catch (error) {
    }
};

/** Курс, полученный с сервера. В отличие от запасных значений выше, его можно показывать как настоящий. */
export const getKnownUsdRate = () => {
    try {
        const rate = Number(localStorage.getItem(KNOWN_USD_RATE_STORAGE_KEY));
        return rate > 0 ? rate : null;
    } catch {
        return null;
    }
};

const saveKnownUsdRate = (rate) => {
    try {
        if (rate) {
            localStorage.setItem(KNOWN_USD_RATE_STORAGE_KEY, String(rate));
        } else {
            localStorage.removeItem(KNOWN_USD_RATE_STORAGE_KEY);
        }
    } catch {
        // Хранилище недоступно: итог покажется без пересчёта
    }
};

export const setExchangeRates = (ratesToUah) => {
    const nextRates = { ...DEFAULT_EXCHANGE_RATES };
    const normalizedRates = {};

    Object.entries(ratesToUah || {}).forEach(([currency, rate]) => {
        const code = normalizeCurrency(currency);
        const numericRate = Number(rate);
        if (!numericRate || numericRate <= 0) return;
        normalizedRates[code] = numericRate;
    });

    normalizedRates.UAH = 1.0;
    saveKnownUsdRate(normalizedRates.USD);

    const currencies = Object.keys(normalizedRates);
    currencies.forEach((from) => {
        currencies.forEach((to) => {
            if (from === to) {
                nextRates[`${from}/${to}`] = 1.0;
                return;
            }

            const fromToUah = normalizedRates[from];
            const toToUah = normalizedRates[to];
            if (!fromToUah || !toToUah) return;

            nextRates[`${from}/${to}`] = Number((fromToUah / toToUah).toFixed(8));
        });
    });

    runtimeExchangeRates = nextRates;
    saveRatesToStorage(nextRates);
};

export const getExchangeRates = () => ({ ...runtimeExchangeRates });

export const CURRENCY_SYMBOLS = {
    USD: '$',
    UAH: '₴',
    EUR: '€',
};

export const CURRENCY_NAMES = {
    USD: 'Доллар США',
    UAH: 'Украинская гривня',
    EUR: 'Евро',
};

/**
 * Конвертирует сумму из одной валюты в другую
 * @param {number} amount - Сумма
 * @param {string} fromCurrency - Из какой валюты (USD, UAH, usd, uah)
 * @param {string} toCurrency - В какую валюту
 * @returns {number} Конвертированная сумма
 */
export const convertCurrency = (amount, fromCurrency, toCurrency) => {
    const from = normalizeCurrency(fromCurrency);
    const to = normalizeCurrency(toCurrency);
    
    if (from === to) {
        return amount;
    }

    const key = `${from}/${to}`;
    if (!(key in runtimeExchangeRates)) {
        console.warn(`Курс обмена для ${key} не найден`);
        return amount;
    }

    return Number((amount * runtimeExchangeRates[key]).toFixed(2));
};

/**
 * Форматирует сумму в указанной валюте
 * @param {number} amount - Сумма
 * @param {string} currency - Код валюты (USD, UAH, usd, uah)
 * @returns {string} Форматированная строка
 */
export const formatCurrency = (amount, currency = 'UAH') => {
    const normalized = normalizeCurrency(currency);
    const symbol = CURRENCY_SYMBOLS[normalized] || normalized;

    if (!amount && amount !== 0) {
        return '—';
    }

    const formatted = Number(amount).toLocaleString('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    if (normalized === 'UAH') {
        // Для гривни: наименование после числа
        return `${formatted} ${symbol}`;
    } else {
        // Для доллара: символ перед числом
        return `${symbol}${formatted}`;
    }
};

/**
 * Форматирует сумму с конвертацией в целевую валюту
 * @param {number} amount - Сумма
 * @param {string} fromCurrency - Из какой валюты
 * @param {string} toCurrency - В какую валюту
 * @returns {string} Форматированная и конвертированная строка
 */
export const formatCurrencyConverted = (amount, fromCurrency, toCurrency) => {
    const from = normalizeCurrency(fromCurrency);
    const to = normalizeCurrency(toCurrency);
    
    if (from === to) {
        return formatCurrency(amount, to);
    }

    const converted = convertCurrency(amount, from, to);
    return formatCurrency(converted, to);
};

/**
 * Получает информацию о валюте
 * @param {string} currency - Код валюты
 * @returns {object} Объект с информацией
 */
export const getCurrencyInfo = (currency) => {
    const normalized = normalizeCurrency(currency);
    return {
        code: normalized,
        symbol: CURRENCY_SYMBOLS[normalized] || normalized,
        name: CURRENCY_NAMES[normalized] || normalized,
    };
};

/**
 * Хук для получения текущей системной валюты из localStorage
 * @returns {string} Код валюты (заглавные: UAH или USD)
 */
export const getSystemCurrency = () => {
    const stored = localStorage.getItem('systemCurrency');
    return normalizeCurrency(stored || 'UAH');
};

/**
 * Хук для установки системной валюты в localStorage
 * @param {string} currency - Код валюты (заглавные: UAH или USD)
 */
export const setSystemCurrency = (currency) => {
    const normalized = normalizeCurrency(currency);
    localStorage.setItem('systemCurrency', normalized);
    // Отправляем событие для обновления компонентов
    window.dispatchEvent(new CustomEvent('currencyChanged', { detail: { currency: normalized } }));
};
